import { Text, HStack } from "@chakra-ui/react"
import { ColumnDef } from "@tanstack/react-table"
import { Customer, TenantProfile } from "@hydrologiq/gql-experience/graphql"
import { CustomerExpandedSites } from "./sites/CustomerExpandedSites"
import { useNavigate } from "react-router-dom"
import CustomerHydrogenTotal from "./monitoring/CustomerHydrogenTotal"
import CustomerRunDry from "./forecast/CustomerRunDry"
import { Tooltip } from "@hydrologiq/shared/chakra/Tooltip"
import { useRoute } from "@hydrologiq/shared/providers/helpers/useRoute"
import { useGetProfile } from "@hydrologiq/shared/providers/helpers/useProfile"
import { useGetFeature } from "@hydrologiq/shared/providers/helpers/useFeature"
import { useCopyValues } from "@hydrologiq/shared/providers/helpers/useCopy"
import { COPY_KEYS, DEFAULT_COPY_MAPPINGS } from "@hydrologiq/shared/copy/constants"
import { PaginatedTable, ServerPagination } from "@hydrologiq/shared/components/PaginatedTable"
import { PaginationMeta, SortConfig } from "@hydrologiq/shared/types/pagination"
import { MultiOrderDisplay } from "@hydrologiq/shared/components/activities"
import CustomerAlertsPopup from "./popups/CustomerAlertsPopup"
import CustomerAssetsPopup from "./popups/CustomerAssetsPopup"
import Link from "@hydrologiq/shared/components/Link"
import SystemRunDryIcon from "./sites/SystemRunDryIcon"

interface CustomersTablePaginatedProps {
  customers: Customer[]
  loading?: boolean
  error?: string | null
  onRetry?: () => void
  pagination?: PaginationMeta
  onPageChange?: (page: number) => void
  onPageSizeChange?: (pageSize: number) => void
  sort?: SortConfig | null
  onSortChange?: (sort: SortConfig | null) => void
  headerActions?: React.ReactNode
}

function CustomersTablePaginated({
  customers,
  loading,
  error,
  onRetry,
  pagination,
  onPageChange,
  onPageSizeChange,
  sort,
  onSortChange,
  headerActions,
}: CustomersTablePaginatedProps) {
  const route = useRoute()
  const navigate = useNavigate()
  const profile = useGetProfile()

  const isRentalProfile = profile === TenantProfile.Rental

  const assetDepletionEnabled = useGetFeature("asset_depletion")

  const copy = useCopyValues({
    [COPY_KEYS.HEADINGS.CUSTOMER]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.CUSTOMER],
    [COPY_KEYS.HEADINGS.NAME]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.NAME],
    [COPY_KEYS.HEADINGS.CURRENT_ORDER]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.CURRENT_ORDER],
    [COPY_KEYS.HEADINGS.ASSETS]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ASSETS],

    [COPY_KEYS.HEADINGS.RUN_DRY]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.RUN_DRY],
    [COPY_KEYS.HEADINGS.USAGE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.USAGE],
    [COPY_KEYS.HEADINGS.HYDROGEN_ORDERED]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.HYDROGEN_ORDERED],
    [COPY_KEYS.HEADINGS.HYDROGEN_CONSUMED]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.HYDROGEN_CONSUMED],
    [COPY_KEYS.HEADINGS.HYDROGEN_RETURNED]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.HYDROGEN_RETURNED],
    [COPY_KEYS.HEADINGS.HYDROGEN_RETURNED_PER_FULFILLMENT]:
      DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.HYDROGEN_RETURNED_PER_FULFILLMENT],
    [COPY_KEYS.HEADINGS.AVERAGE_ORDER]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.AVERAGE_ORDER],
    [COPY_KEYS.HEADINGS.TIME_TO_DEPLETION]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.TIME_TO_DEPLETION],
  })

  // Define base columns that are always shown
  const baseColumns: ColumnDef<Customer, any>[] = [
    {
      id: "name",
      accessorKey: "name",
      header: copy[COPY_KEYS.HEADINGS.NAME],
      cell: ({ row }) => {
        const customer = row.original
        const customerPath = `${route.endsWith("/") ? route : route + "/"}${customer.id}`
        return (
          <Tooltip content={`Explore ${customer.name}`}>
            <Link variant="primary" href={customerPath}>
              {customer.name.length > 35 ? `${customer.name.slice(0, 35)}...` : customer.name}
            </Link>
          </Tooltip>
        )
      },
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      id: "assets",
      header: `${copy[COPY_KEYS.HEADINGS.ASSETS]} in use`,
      cell: ({ row }) => (
        <CustomerAssetsPopup customer={row.original} assetCount={row.original.assetsOnHireCount || 0} />
      ),
      enableSorting: false,
    },
    {
      id: "alerts",
      header: "Alerts",
      cell: ({ row }) => <CustomerAlertsPopup customer={row.original} alertCount={row.original.alertCount || 0} />,
      enableSorting: false,
    },
  ]

  // Conditionally add asset depletion column (controlled by feature toggle)
  if (assetDepletionEnabled) {
    baseColumns.push({
      id: "run_dry",
      header: () => (
        <Tooltip content="Earliest asset run dry time">
          <HStack justify="end">
            <Text>{copy[COPY_KEYS.HEADINGS.TIME_TO_DEPLETION]}</Text>
          </HStack>
        </Tooltip>
      ),
      cell: ({ row }) => <CustomerRunDry customer={row.original} />,
      enableSorting: false,
    })
  }

  // Build profile-specific columns
  const profileColumns: ColumnDef<Customer, any>[] = []

  if (isRentalProfile) {
    // Rental-specific metrics
    profileColumns.push(
      {
        id: "installation_run_dry",
        header: () => (
          <Tooltip content="Earliest installation run dry time">
            <HStack justify="center" width="100%">
              <Text>Refuel needed in</Text>
            </HStack>
          </Tooltip>
        ),
        cell: ({ row }) => <SystemRunDryIcon runDryMinutes={row.original.earliestInstallationRunDryMinutes ?? undefined} system={row.original.earliestInstallationRunDrySystem?.name ?? undefined} systemId={row.original.earliestInstallationRunDrySystem?.id ?? undefined} />,
        enableSorting: false,
      },
      {
        id: "energy",
        accessorKey: "energy",
        header: "Energy Yesterday (kWh)",
        cell: ({ row }) => (
          <Text textAlign="end">{row.original.energy ? `${row.original.energy.toFixed(2)} kWh` : "-"}</Text>
        ),
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        id: "co2e",
        accessorKey: "co2e",
        header: "CO2e Yesterday (kg)",
        cell: ({ row }) => (
          <Text textAlign="end">{row.original.co2e ? `${row.original.co2e.toFixed(2)} kg` : "-"}</Text>
        ),
        enableSorting: true,
        enableColumnFilter: false,
      }
    )
  } else {
    // Fuel producer profile columns
    profileColumns.push(
      {
        id: "current_orders",
        header: copy[COPY_KEYS.HEADINGS.CURRENT_ORDER],
        cell: ({ row }) => {
          const customer = row.original
          return customer.currentOrders && customer.currentOrders.length > 0 ? (
            <MultiOrderDisplay
              orders={customer.currentOrders.map((order) => ({
                id: order.id,
                name: order.name,
                status: order.status,
                start: order.start || "",
                end: order.end || "",
              }))}
              maxVisible={1}
            />
          ) : (
            <Text color="gray.500">-</Text>
          )
        },
        enableSorting: false,
      },
      {
        id: "hydrogen_on_site",
        header: () => (
          <Tooltip content="Total hydrogen currently on all customer sites">
            <HStack justify="start">
              <Text>Hydrogen on Site (kg)</Text>
            </HStack>
          </Tooltip>
        ),
        cell: ({ row }) => <CustomerHydrogenTotal customer={row.original} />,
        enableSorting: false,
      },
      {
        id: "hydrogen_ordered",
        accessorKey: "hydrogenOrdered",
        header: copy[COPY_KEYS.HEADINGS.HYDROGEN_ORDERED],
        cell: ({ row }) => (
          <Text textAlign="end">{row.original.hydrogenOrdered ? row.original.hydrogenOrdered.toFixed(2) : "-"}</Text>
        ),
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        id: "hydrogen_consumed",
        accessorKey: "hydrogen_consumed",
        header: copy[COPY_KEYS.HEADINGS.HYDROGEN_CONSUMED],
        cell: ({ row }) => (
          <Text textAlign="end">{row.original.hydrogenConsumed ? row.original.hydrogenConsumed.toFixed(2) : "-"}</Text>
        ),
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        id: "hydrogen_returned",
        accessorKey: "hydrogenReturned",
        header: copy[COPY_KEYS.HEADINGS.HYDROGEN_RETURNED],
        cell: ({ row }) => (
          <Text textAlign="end">{row.original.hydrogenReturned ? row.original.hydrogenReturned.toFixed(2) : "-"}</Text>
        ),
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        id: "hydrogen_returned_per_fulfillment",
        accessorKey: "hydrogenReturnedPerFulfillment",
        header: copy[COPY_KEYS.HEADINGS.HYDROGEN_RETURNED_PER_FULFILLMENT],
        cell: ({ row }) => (
          <Text textAlign="end">
            {row.original.hydrogenReturnedPerFulfillment ? row.original.hydrogenReturnedPerFulfillment.toFixed(2) : "-"}
          </Text>
        ),
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        id: "average_order",
        accessorKey: "averageOrder",
        header: copy[COPY_KEYS.HEADINGS.AVERAGE_ORDER],
        cell: ({ row }) => (
          <Text textAlign="end">{row.original.averageOrder ? row.original.averageOrder.toFixed(2) : "-"}</Text>
        ),
        enableSorting: true,
        enableColumnFilter: false,
      }
    )
  }

  // Combine base columns with profile-specific columns
  const columns = [...baseColumns, ...profileColumns]

  // Setup server pagination if provided
  const serverPagination: ServerPagination | undefined = pagination
    ? {
      pagination,
      onPageChange: onPageChange || (() => { }),
      onPageSizeChange: onPageSizeChange || (() => { }),
    }
    : undefined

  return (
    <PaginatedTable
      data={customers}
      columns={columns}
      loading={loading}
      error={error}
      onRetry={onRetry}
      serverPagination={serverPagination}
      serverFiltering={
        sort && onSortChange
          ? {
            filters: {},
            sort: sort,
            onFiltersChange: () => { },
            onSortChange: onSortChange,
            onClearAll: () => { },
          }
          : undefined
      }
      emptyState={{
        heading: "No customers",
        text: `Please create a ${copy[COPY_KEYS.HEADINGS.CUSTOMER].toLowerCase()}`,
      }}
      variant="hydrologiq"
      entityName="customers"
      headerActions={headerActions}
      expandableRows={{
        renderExpandedContent: (customer: Customer) => <CustomerExpandedSites customer={customer} />,
        getRowId: (customer: Customer) => customer.id,
      }}
    />
  )
}

export default CustomersTablePaginated
